import { api } from '@/lib/api';
import type {
  Invoice,
  InvoiceDocument,
  InvoicesFilters,
  InvoicesResponse,
  CreateInvoiceInput,
  UpdateInvoiceInput,
  AddInvoiceNoteInput,
  Organisation,
  User,
  InvoiceStatusStats,
  V4Invoice,
  V4InvoicesFilters,
  V4InvoicesResponse,
  V4InvoiceStatsResponse,
  PopulatedSubscription,
} from './types';
import { transformV4InvoiceToV3 } from './types';

// =============================================================================
// Invoices API Functions (V4 - PostgreSQL)
// Data source: PostgreSQL via GET /v4/invoices
// =============================================================================

/**
 * Get invoices with optional filtering, sorting, and pagination (V4)
 */
export async function getInvoices(filters: InvoicesFilters = {}): Promise<InvoicesResponse> {
  // Map V3-style filters to V4 camelCase format
  const queryParams: Record<string, string | number> = {
    pageNumber: filters.pageNumber ?? 0,
    pageLength: filters.pageLength ?? 25,
    sortDirection: filters.sortDirection ?? -1,
  };

  // Map sort keys from snake_case to camelCase
  if (filters.sortKey) {
    const sortKeyMap: Record<string, string> = {
      invoice_number: 'invoiceNumber',
      created_at: 'createdAt',
      date: 'invoiceDate',
      expiration_date: 'dueDate',
      payment_date: 'paymentDate',
      total: 'total',
      customer: 'accountName',
    };
    queryParams.sortKey = sortKeyMap[filters.sortKey] || filters.sortKey;
  } else {
    queryParams.sortKey = 'createdAt';
  }

  // Add search filter
  if (filters.search) {
    queryParams.search = filters.search;
  }

  // Add account filter (V4 uses accountId instead of customer)
  if (filters.customer) {
    // Note: V4 expects accountId (UUID), but customer might be MongoDB ObjectId
    // For now, pass as-is and let backend handle the mapping
    queryParams.accountId = filters.customer;
  }

  // Add status filter
  if (filters.status) {
    if (Array.isArray(filters.status)) {
      queryParams.status_in = JSON.stringify(filters.status);
    } else {
      queryParams.status = filters.status;
    }
  }

  // Add sales manager filter
  if (filters.sales_manager) {
    queryParams.salesManagerId = filters.sales_manager;
  }

  // Add contractual manager filter
  if (filters.contractual_manager) {
    queryParams.contractualManagerId = filters.contractual_manager;
  }

  // Multi-select ID filters (from searchable dropdowns)
  if (filters.account_id_in && filters.account_id_in.length > 0) {
    queryParams.accountIdIn = JSON.stringify(filters.account_id_in);
  }
  if (filters.sales_manager_id_in && filters.sales_manager_id_in.length > 0) {
    queryParams.salesManagerIdIn = JSON.stringify(filters.sales_manager_id_in);
  }

  // Add date range filters (map to camelCase)
  if (filters.date_from) {
    queryParams.dateFrom = filters.date_from;
  }
  if (filters.date_to) {
    queryParams.dateTo = filters.date_to;
  }
  if (filters.expiration_date_from) {
    queryParams.expirationDateFrom = filters.expiration_date_from;
  }
  if (filters.expiration_date_to) {
    queryParams.expirationDateTo = filters.expiration_date_to;
  }
  if (filters.payment_date_from) {
    queryParams.paymentDateFrom = filters.payment_date_from;
  }
  if (filters.payment_date_to) {
    queryParams.paymentDateTo = filters.payment_date_to;
  }

  // Column-level filters
  if (filters.invoice_number_search) {
    queryParams.invoiceNumberSearch = filters.invoice_number_search;
  }
  if (filters.account_name_search) {
    queryParams.accountNameSearch = filters.account_name_search;
  }
  if (filters.created_at_from) {
    queryParams.createdAtFrom = filters.created_at_from;
  }
  if (filters.created_at_to) {
    queryParams.createdAtTo = filters.created_at_to;
  }
  if (filters.po_number) {
    queryParams.poNumber = filters.po_number;
  }
  if (filters.project_name) {
    queryParams.projectName = filters.project_name;
  }
  if (filters.next_step) {
    queryParams.nextStep = filters.next_step;
  }
  if (filters.next_step_deadline_from) {
    queryParams.nextStepDeadlineFrom = filters.next_step_deadline_from;
  }
  if (filters.next_step_deadline_to) {
    queryParams.nextStepDeadlineTo = filters.next_step_deadline_to;
  }
  if (filters.currency) {
    queryParams.currency = filters.currency;
  }
  if (filters.contract_search) {
    queryParams.contractSearch = filters.contract_search;
  }
  if (filters.total_min) {
    queryParams.totalMin = filters.total_min;
  }
  if (filters.total_max) {
    queryParams.totalMax = filters.total_max;
  }
  if (filters.payment_terms_in && filters.payment_terms_in.length > 0) {
    queryParams.paymentTermsIn = JSON.stringify(filters.payment_terms_in);
  }
  if (filters.tax_category_in && filters.tax_category_in.length > 0) {
    queryParams.taxCategoryIn = JSON.stringify(filters.tax_category_in);
  }
  if (filters.sales_manager_search) {
    queryParams.salesManagerSearch = filters.sales_manager_search;
  }
  if (filters.contractual_manager_search) {
    queryParams.contractualManagerSearch = filters.contractual_manager_search;
  }

  const response = await api.get<V4InvoicesResponse>('/v4/invoices', queryParams);

  // Transform V4 invoices to V3-compatible format for backward compatibility
  const invoices: Invoice[] = response.invoices.map(transformV4InvoiceToV3);

  // Fetch stats separately for V4 — forward all active filters so stats match the table
  const statsResponse = await getInvoiceStats({
    accountId: filters.customer,
    salesManagerId: filters.sales_manager,
    contractualManagerId: filters.contractual_manager,
    dateFrom: filters.date_from,
    dateTo: filters.date_to,
  });

  return {
    invoices,
    stats: statsResponse,
    pageNumber: response.pageNumber,
    pageLength: response.pageLength,
    total: response.total,
  };
}

/**
 * Get invoice stats by status (V4)
 */
export async function getInvoiceStats(
  filters: {
    accountId?: string;
    salesManagerId?: string;
    contractualManagerId?: string;
    dateFrom?: string;
    dateTo?: string;
  } = {}
): Promise<InvoiceStatusStats[]> {
  const queryParams: Record<string, string> = {};

  if (filters.accountId) {
    queryParams.accountId = filters.accountId;
  }
  if (filters.salesManagerId) {
    queryParams.salesManagerId = filters.salesManagerId;
  }
  if (filters.contractualManagerId) {
    queryParams.contractualManagerId = filters.contractualManagerId;
  }
  if (filters.dateFrom) {
    queryParams.dateFrom = filters.dateFrom;
  }
  if (filters.dateTo) {
    queryParams.dateTo = filters.dateTo;
  }

  const response = await api.get<V4InvoiceStatsResponse>('/v4/invoices/stats', queryParams);

  // Map V4 stats to V3 format (quantity/amount)
  return response.stats.map((stat) => ({
    status: stat.status as InvoiceStatusStats['status'],
    quantity: stat.count,
    amount: stat.amount,
  }));
}

/**
 * Get invoices directly as V4 format (for new code that doesn't need V3 compatibility)
 */
export async function getInvoicesV4(filters: V4InvoicesFilters = {}): Promise<V4InvoicesResponse> {
  const queryParams: Record<string, string | number> = {
    pageNumber: filters.pageNumber ?? 0,
    pageLength: filters.pageLength ?? 25,
    sortKey: filters.sortKey ?? 'createdAt',
    sortDirection: filters.sortDirection ?? -1,
  };

  if (filters.search) {
    queryParams.search = filters.search;
  }
  if (filters.accountId) {
    queryParams.accountId = filters.accountId;
  }
  if (filters.status) {
    queryParams.status = filters.status;
  }
  if (filters.status_in) {
    queryParams.status_in = JSON.stringify(filters.status_in);
  }
  if (filters.salesManagerId) {
    queryParams.salesManagerId = filters.salesManagerId;
  }
  if (filters.contractualManagerId) {
    queryParams.contractualManagerId = filters.contractualManagerId;
  }
  if (filters.dateFrom) {
    queryParams.dateFrom = filters.dateFrom;
  }
  if (filters.dateTo) {
    queryParams.dateTo = filters.dateTo;
  }
  if (filters.expirationDateFrom) {
    queryParams.expirationDateFrom = filters.expirationDateFrom;
  }
  if (filters.expirationDateTo) {
    queryParams.expirationDateTo = filters.expirationDateTo;
  }
  if (filters.paymentDateFrom) {
    queryParams.paymentDateFrom = filters.paymentDateFrom;
  }
  if (filters.paymentDateTo) {
    queryParams.paymentDateTo = filters.paymentDateTo;
  }

  return api.get<V4InvoicesResponse>('/v4/invoices', queryParams);
}

/**
 * Get a single invoice by ID (V4)
 */
export async function getInvoiceV4(invoiceId: string): Promise<V4Invoice> {
  return api.get<V4Invoice>(`/v4/invoices/${invoiceId}`);
}

/**
 * Get a single invoice by invoice number (V4)
 */
export async function getInvoiceByNumber(invoiceNumber: string): Promise<V4Invoice> {
  return api.get<V4Invoice>(`/v4/invoices/by-number/${encodeURIComponent(invoiceNumber)}`);
}

// =============================================================================
// Legacy V3 Functions (MongoDB - for mutations until V4 write endpoints exist)
// =============================================================================

/**
 * Get a single invoice by ID (V3 - uses MongoDB for full data including SKUs)
 */
export async function getInvoice(invoiceId: string): Promise<Invoice> {
  const response = await api.get<{ invoice: Invoice }>(`/v4/legacy/invoices/${invoiceId}`, {
    populate: JSON.stringify([
      'customer',
      'contract',
      'sales_manager',
      'contractual_manager',
      'po_file',
      'billing_site',
    ]),
  });
  return response.invoice;
}

/**
 * Create a new invoice (V3 - uses MongoDB)
 */
export async function createInvoice(data: CreateInvoiceInput): Promise<Invoice> {
  const response = await api.post<{ invoice: Invoice }>('/v4/legacy/invoices', data);
  const invoice = response.invoice;
  // Link the freshly-created Mongo invoice to its PG account. The accounts_sync CDC that
  // used to populate the PG invoices mirror is decommissioned, so without this the invoice
  // has no PG accountId and is unlinked from its account in all V4 views. Non-fatal.
  if (invoice?._id) {
    await syncInvoiceAccount(invoice._id);
  }
  return invoice;
}

/**
 * Mirror a just-created invoice into the PG `invoices` table with its resolved account_id.
 * Best-effort: a failure here must never block invoice creation.
 */
export async function syncInvoiceAccount(mongoInvoiceId: string): Promise<void> {
  try {
    await api.post('/v4/invoices/sync-account', { mongoInvoiceId });
  } catch (err) {
    console.warn('[invoices] account-link sync failed', mongoInvoiceId, err);
  }
}

/**
 * Update an invoice (V3 - uses MongoDB), then re-materialize its PG mirror row.
 *
 * V4 list/detail views read the PostgreSQL `invoices` mirror, but that mirror is only
 * rebuilt nightly (invoicesResyncJob, 3am) — the real-time accounts_sync CDC is
 * decommissioned. Without an immediate re-sync, an edit written to Mongo (e.g. a status
 * transition preparation → sent → paid) wouldn't surface in V4 until the next 3am run:
 * the list refetches PG and the status "snaps back" to its old value. Mirror the
 * createInvoice pattern and push the change to PG right away via the sync-account seam,
 * which upserts the full row (status + money + managers + dates) by mongoInvoiceId.
 * Non-fatal — a sync failure must never fail the edit itself.
 */
export async function updateInvoice(invoiceId: string, data: UpdateInvoiceInput): Promise<Invoice> {
  const response = await api.put<{ invoice: Invoice }>(`/v4/legacy/invoices/${invoiceId}`, data);
  const invoice = response.invoice;
  if (invoice?._id) {
    await syncInvoiceAccount(invoice._id);
  }
  return invoice;
}

/**
 * Upload a PO (purchase order) file and attach it to an invoice (QUAL-5947).
 *
 * There is no dedicated invoice po-file endpoint: the upload goes through the
 * same legacy files proxy the quote P/O upload was built on
 * (POST /v4/legacy/v2/files/upload/), which responds with the BARE file id as
 * JSON. Persisting the attachment is then a regular invoice update —
 * `po_file` is allowlisted server-side (dao/invoices.js updateOneByID).
 */
export async function uploadInvoicePoFile(invoiceId: string, file: File): Promise<Invoice> {
  const formData = new FormData();
  formData.append('file', file);
  const fileId = await api.upload<string>('/v4/legacy/v2/files/upload/', formData);
  return updateInvoice(invoiceId, { po_file: fileId });
}

/**
 * Detach the PO file from an invoice by clearing `po_file`
 * (the stored file itself is kept in the files store).
 */
export async function deleteInvoicePoFile(invoiceId: string): Promise<Invoice> {
  return updateInvoice(invoiceId, { po_file: null });
}

/**
 * Delete an invoice (soft delete) (V3 - uses MongoDB)
 */
export async function deleteInvoice(invoiceId: string): Promise<void> {
  await api.delete(`/v4/legacy/invoices/${invoiceId}`);
}

/**
 * Add a note to an invoice (V3 - uses MongoDB)
 */
export async function addInvoiceNote(
  invoiceId: string,
  data: AddInvoiceNoteInput
): Promise<Invoice> {
  const response = await api.post<{ invoice: Invoice }>(`/v4/legacy/invoices/${invoiceId}/notes`, data);
  return response.invoice;
}

/**
 * Get invoice HTML for viewing/printing (V3 - uses MongoDB)
 * Note: This endpoint returns raw HTML (not JSON), so we use fetch directly.
 */
export async function getInvoiceHtml(invoiceId: string): Promise<string> {
  const response = await fetch(`/api/v4/legacy/invoices/${invoiceId}/html`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch invoice HTML: ${response.status}`);
  }
  return response.text();
}

// =============================================================================
// Accounts API Functions (for customer filter) — V4 PostgreSQL
// =============================================================================

interface V4AccountsListResponse {
  accounts: Array<{
    id: string;
    name: string;
    accountNumber: string;
    mongoOrganisationId?: string;
    type: string;
    status: string;
  }>;
  total: number;
  pageNumber: number;
  pageLength: number;
}

interface V4AccountResponse {
  account: {
    id: string;
    name: string;
    accountNumber?: string | null;
    mongoOrganisationId?: string | null;
  };
}

/**
 * Get customer accounts for the dropdown filter (V4 - PostgreSQL)
 * Returns Organisation-shaped objects with PG UUID as _id for compatibility
 * with V4 invoice data (which uses accountId UUIDs).
 * Paginates through all pages since API caps at 100 per page.
 */
export async function getCustomerOrganisations(): Promise<Organisation[]> {
  const all: Organisation[] = [];
  let page = 0;
  const pageSize = 100;

  while (true) {
    const response = await api.get<V4AccountsListResponse>('/v4/accounts', {
      type: 'customer',
      pageLength: pageSize,
      pageNumber: page,
      sortKey: 'name',
      sortDirection: 1,
    });

    const accounts = response.accounts || [];
    all.push(
      ...accounts
        .filter((a) => !a.accountNumber?.startsWith('A-'))
        .map((a) => ({
          _id: a.id,
          name: a.name,
          mongoOrgId: a.mongoOrganisationId,
          accountNumber: a.accountNumber,
          status: a.status,
        }))
    );

    if (accounts.length < pageSize) break;
    page++;
  }

  return all;
}

/**
 * Get a single customer account detail so MongoDB writes can resolve the source organisation id.
 */
export async function getCustomerOrganisation(accountId: string): Promise<Organisation> {
  const response = await api.get<V4AccountResponse>(`/v4/accounts/${accountId}`);
  const account = response.account;

  return {
    _id: account.id,
    name: account.name,
    mongoOrgId: account.mongoOrganisationId || undefined,
    accountNumber: account.accountNumber || undefined,
  };
}

// =============================================================================
// Quotes API Functions (V4 - PostgreSQL)
// =============================================================================

export interface AccountQuote {
  id: string;
  quoteNumber: string;
  projectName?: string;
  status: string;
}

interface V4AccountQuotesResponse {
  quotes: AccountQuote[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

/**
 * Get quotes assigned to an account (V4 - PostgreSQL).
 * Fetches all pages since the dropdown needs the full list.
 */
export async function getQuotesByAccount(accountId: string): Promise<AccountQuote[]> {
  const all: AccountQuote[] = [];
  let page = 0;
  const pageSize = 100;

  while (true) {
    const response = await api.get<V4AccountQuotesResponse>(
      `/v4/accounts/${accountId}/quotes`,
      { pageNumber: page, pageLength: pageSize },
    );

    all.push(...(response.quotes || []));
    if ((response.quotes || []).length < pageSize) break;
    page++;
  }

  return all;
}

// =============================================================================
// Operations Projects API Functions (V4 - PostgreSQL)
// =============================================================================

export interface AccountProject {
  id: string;
  projectNumber: string;
  projectName?: string;
  type?: string;
  status?: string;
}

interface V4OperationsProjectsResponse {
  projects: Array<{
    id: string;
    project_number: string;
    project_name?: string;
    type?: string;
    status?: string;
  }>;
  pageNumber: number;
  pageLength: number;
  total: number;
}

/**
 * Get operations projects assigned to an account (V4 - PostgreSQL).
 * Fetches all pages since the dropdown needs the full list.
 */
export async function getProjectsByAccount(accountId: string): Promise<AccountProject[]> {
  const all: AccountProject[] = [];
  let page = 0;
  const pageSize = 100;

  while (true) {
    const response = await api.get<V4OperationsProjectsResponse>(
      '/v4/operations-projects',
      { accountId, pageNumber: page, pageLength: pageSize, sortKey: 'project_number', sortDirection: -1 },
    );

    const projects = response.projects || [];
    all.push(
      ...projects.map((p) => ({
        id: p.id,
        projectNumber: p.project_number,
        projectName: p.project_name,
        type: p.type,
        status: p.status,
      }))
    );

    if (projects.length < pageSize) break;
    page++;
  }

  return all;
}

// =============================================================================
// Users API Functions (for manager filters)
// =============================================================================

/**
 * Get sales users for the sales manager dropdown.
 * V4 (PostgreSQL) only — ATEK staff = employees + contractors.
 */
export async function getSalesUsers(): Promise<User[]> {
  const response = await api.get<{
    users: Array<{ pg_id: string; firstname?: string; lastname?: string; email?: string }>;
    total: number;
  }>('/v4/users', {
    pageLength: 100,
    sortKey: 'firstname',
    sortDirection: 1,
    employment_type: 'employee,contractor',
    enabled: 'true',
    is_subaccount: 'false',
  });
  return (response.users || []).map((u) => ({
    _id: u.pg_id,
    firstname: u.firstname,
    lastname: u.lastname,
    email: u.email,
  })) as User[];
}

// =============================================================================
// Contracts API Functions (for create invoice modal) — V4 PostgreSQL
// =============================================================================

interface V4SubscriptionsListResponse {
  subscriptions: Array<{
    id: string;
    subscriptionNumber: string;
    name: string;
    mongoContractId?: string | null;
  }>;
  total: number;
}

/**
 * Get contracts filtered by customer.
 * Accepts either a PG accountId (UUID) or a MongoDB customerId (ObjectId string).
 * When pgAccountId is provided, it filters server-side via the V4 endpoint.
 */
export async function getSubscriptionsByCustomer(_customerId: string, pgAccountId?: string): Promise<PopulatedSubscription[]> {
  const response = await api.get<V4SubscriptionsListResponse>('/v4/subscriptions', {
    pageLength: 100,
    sortKey: 'name',
    sortDirection: 1,
    ...(pgAccountId ? { accountId: pgAccountId } : {}),
  });

  return (response.subscriptions || []).map((c) => ({
    _id: c.id,
    mongoContractId: c.mongoContractId || undefined,
    name: c.name,
    contract_number: c.subscriptionNumber,
  }));
}

/** @deprecated Use getSubscriptionsByCustomer instead */
export const getContractsByCustomer = getSubscriptionsByCustomer;

// =============================================================================
// Contract (full Mongo doc) API — for fill-from-contract feature
// =============================================================================

/**
 * Full Mongo contract document returned by GET /v4/legacy/subscriptions/:id
 */
export interface ContractDoc {
  _id: string;
  customer?: string;
  sales_manager?: string;
  payment_terms?: string;
  billing_sites?: string[];
  shipping_sites?: string[];
  contractual_managers?: string[];
  pricelists?: string[];
  language?: string;
  currency?: string;
}

/**
 * Fetch the full Mongo contract document by its Mongo ObjectId.
 * Used by the "Fill from subscription" feature to populate invoice form fields.
 */
export async function getContractById(contractId: string): Promise<ContractDoc> {
  const response = await api.get<{ contract: ContractDoc }>(
    `/v4/legacy/subscriptions/${contractId}`,
  );
  return response.contract;
}

// =============================================================================
// Invoice Documents API (DMS links)
// =============================================================================

/**
 * Get all DMS documents linked to an invoice
 */
export async function getInvoiceDocuments(invoiceId: string): Promise<InvoiceDocument[]> {
  const response = await api.get<{ documents: InvoiceDocument[] }>(`/v4/invoices/${invoiceId}/documents`);
  return response.documents;
}

/**
 * Link a DMS document to an invoice
 */
export async function addInvoiceDocument(invoiceId: string, documentId: string): Promise<InvoiceDocument> {
  return api.post<InvoiceDocument>(`/v4/invoices/${invoiceId}/documents`, { documentId });
}

/**
 * Unlink a DMS document from an invoice
 */
export async function removeInvoiceDocument(invoiceId: string, documentId: string): Promise<void> {
  await api.delete(`/v4/invoices/${invoiceId}/documents/${documentId}`);
}

// =============================================================================
// Invoice Sensors API (Mongo sensor ↔ invoice linkage)
// Endpoints: POST /v4/legacy/invoices/:invoiceId/add-sensor
//            POST /v4/legacy/invoices/:invoiceId/remove-sensor
// `:invoiceId` is the invoice's Mongo `_id`.
// =============================================================================

export interface SensorLinkResult {
  ok: boolean;
  invoice_number: string;
}

/**
 * Link a sensor to an invoice (adds invoice_number to sensor's `invoices` array)
 */
export async function addSensorToInvoice(
  invoiceId: string,
  sensorId: string,
): Promise<SensorLinkResult> {
  return api.post<SensorLinkResult>(
    `/v4/legacy/invoices/${invoiceId}/add-sensor`,
    { sensorId },
  );
}

/**
 * Unlink a sensor from an invoice (removes invoice_number from sensor's `invoices` array)
 */
export async function removeSensorFromInvoice(
  invoiceId: string,
  sensorId: string,
): Promise<SensorLinkResult> {
  return api.post<SensorLinkResult>(
    `/v4/legacy/invoices/${invoiceId}/remove-sensor`,
    { sensorId },
  );
}

// =============================================================================
// Invoice Activities API (engagement / audit timeline)
// =============================================================================

export interface InvoiceActivity {
  id: string;
  accountId: string;
  activityType: string;
  description: string;
  relatedEntityType: string | null;
  relatedEntityId: string | null;
  relatedMongoId: string | null;
  performedById: string | null;
  performedByName: string | null;
  metadata: Record<string, unknown> | string | null;
  createdAt: string;
}

interface InvoiceActivitiesResponse {
  activities: InvoiceActivity[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

/**
 * Get the activity timeline for an invoice (notes, status changes, document
 * links, reassignments, etc.). Backed by `account_activities` filtered on
 * the polymorphic columns until issue #368 ships the rename to `activities`.
 */
export async function getInvoiceActivities(
  invoiceId: string,
  params: { pageNumber?: number; pageLength?: number } = {},
): Promise<InvoiceActivitiesResponse> {
  return api.get<InvoiceActivitiesResponse>(`/v4/invoices/${invoiceId}/activities`, params);
}

/** Append a manual activity entry (note/comment) to an invoice timeline. */
export async function addInvoiceActivity(
  invoiceId: string,
  body: { activityType: string; description: string; metadata?: Record<string, unknown> },
): Promise<InvoiceActivity> {
  return api.post<InvoiceActivity>(`/v4/invoices/${invoiceId}/activities`, body);
}

/**
 * Get users belonging to a specific account (V4 PostgreSQL usergroup-members)
 */
export async function getOrganisationUsers(accountId: string): Promise<User[]> {
  const response = await api.get<{
    members: Array<{
      mongoUserId: string;
      firstname?: string;
      lastname?: string;
      email?: string;
      tags?: string[];
      enabled: boolean;
    }>;
  }>(`/v4/accounts/${accountId}/usergroup-members`, {
    enabled: 'true',
    pageLength: 100,
  });

  // Deduplicate by mongoUserId (same person can appear in multiple usergroups)
  const byUser = new Map<string, User>();
  for (const m of response.members || []) {
    if (!byUser.has(m.mongoUserId)) {
      byUser.set(m.mongoUserId, {
        _id: m.mongoUserId,
        firstname: m.firstname,
        lastname: m.lastname,
        email: m.email,
      });
    }
  }
  return Array.from(byUser.values());
}
